/**
 * Simplified Dietician Registration Validation Hook
 *
 * Provides comprehensive validation functions for the dietician registration workflow.
 * Organized into logical groups with reduced duplication and improved maintainability.
 *
 * @module hooks/useDieticianValidation
 */

import { useCallback } from 'react';
import type { HealthCondition } from '../types/referral';
import type { PhoneData, DieticianRegistrationData } from '../types/dieticianRegistration';
import type { LanguageCode } from '@/types/languageCodes';
import type { ValidationResult } from '../types/registration';
import { validateEmail as validateEmailUtil } from '../utils/validation/emailValidation';
import { validatePassword as validatePasswordUtil } from '../utils/validation/passwordValidation';
import { validatePhoneNumber as validatePhoneUtil } from '../utils/validation/phoneValidation';
import { AUSTRALIAN_STATES } from '../constants/geographic';
import { MONEY_RE, normaliseMoneyText } from '@/utils/feePolicy';

export function useDieticianValidation() {
  // ============================================================================
  // INTERNAL VALIDATION GROUPS
  // ============================================================================

  /**
   * Personal Information Validations
   * Groups: email, password, names
   */
  const personalValidations = {
    email: useCallback((email: string): ValidationResult => {
      if (email.length === 0) {
        return { isValid: false, error: 'Email is required' };
      }

      const emailResult = validateEmailUtil(email);
      if (!emailResult.isValid) {
        return emailResult;
      }

      return { isValid: true };
    }, []),

    password: useCallback((password: string): ValidationResult => {
      return validatePasswordUtil(password);
    }, []),

    name: useCallback((name: string, fieldName: string): ValidationResult => {
      if (name.length === 0) {
        return { isValid: false, error: `${fieldName} is required` };
      }

      if (name.length < 2) {
        return { isValid: false, error: `${fieldName} must be at least 2 characters` };
      }

      if (name.length > 50) {
        return { isValid: false, error: `${fieldName} must be less than 50 characters` };
      }

      const namePattern = /^[a-zA-Z\s\-']+$/;
      if (!namePattern.test(name)) {
        return { isValid: false, error: `${fieldName} contains invalid characters` };
      }

      return { isValid: true };
    }, [])
  };

  /**
   * Professional Information Validations
   * Groups: APD number, areas of practice, phone
   */
  const professionalValidations = {
    apdNumber: useCallback((apdNumber: string): ValidationResult => {
      if (apdNumber.length === 0) {
        return { isValid: false, error: 'APD number is required' };
      }

      // APD number format: APDXXXXXX where X is a number (6 digits after APD)
      const apdPattern = /^APD\d{6}$/i;

      if (!apdPattern.test(apdNumber)) {
        return { isValid: false, error: 'Invalid APD number format (e.g., APD123456)' };
      }

      return { isValid: true };
    }, []),

    areasOfPractice: useCallback((areas: HealthCondition[]): ValidationResult => {
      if (areas.length === 0) {
        return { isValid: false, error: 'At least one area of practice is required' };
      }

      if (areas.length > 10) {
        return { isValid: false, error: 'Maximum 10 areas of practice allowed' };
      }

      return { isValid: true };
    }, []),

    languages: useCallback((languages: LanguageCode[]): ValidationResult => {
      if (!languages || languages.length === 0) {
        return { isValid: false, error: 'Select at least one spoken language' };
      }
      return { isValid: true };
    }, []),

    phone: useCallback((phone: PhoneData): ValidationResult => {
      // Convert PhoneData to PhoneNumber format for validation
      const phoneNumber = {
        ...phone,
        formatted: `${phone.countryCode} ${phone.number}`,
        international: `${phone.countryCode}${phone.number}`
      };
      return validatePhoneUtil(phoneNumber);
    }, [])
  };

  /**
   * Address Information Validations
   * Groups: state, city, postal code, country, street address
   * Note: Consolidated state validation (removed duplicate validateAddressState)
   */
  const addressValidations = {
    state: useCallback((state: string): ValidationResult => {
      if (!state?.trim()) {
        return { isValid: false, error: 'State is required' };
      }

      if (!AUSTRALIAN_STATES.includes(state)) {
        return { isValid: false, error: 'Invalid Australian state' };
      }

      return { isValid: true };
    }, []),

    city: useCallback((city: string): ValidationResult => {
      if (!city?.trim()) {
        return { isValid: false, error: 'City is required' };
      }
      if (city.length < 2) {
        return { isValid: false, error: 'City must be at least 2 characters' };
      }
      if (city.length > 50) {
        return { isValid: false, error: 'City must be less than 50 characters' };
      }
      const cityPattern = /^[a-zA-Z\s\-']+$/;
      if (!cityPattern.test(city)) {
        return { isValid: false, error: 'City contains invalid characters' };
      }
      return { isValid: true };
    }, []),

    streetAddress: useCallback((streetAddress: string): ValidationResult => {
      if (!streetAddress?.trim()) {
        return { isValid: false, error: 'Street address is required' };
      }
      if (streetAddress.length < 5) {
        return { isValid: false, error: 'Street address must be at least 5 characters' };
      }
      if (streetAddress.length > 100) {
        return { isValid: false, error: 'Street address must be less than 100 characters' };
      }
      return { isValid: true };
    }, []),

    postalCode: useCallback((postalCode: string): ValidationResult => {
      if (!postalCode?.trim()) {
        return { isValid: false, error: 'Postal code is required' };
      }
      const postalCodePattern = /^\d{4}$/;
      if (!postalCodePattern.test(postalCode)) {
        return { isValid: false, error: 'Postal code must be 4 digits' };
      }
      return { isValid: true };
    }, []),

    country: useCallback((country: string): ValidationResult => {
      if (!country?.trim()) {
        return { isValid: false, error: 'Country is required' };
      }
      // For now, only Australia is supported
      if (country !== 'AU' && country !== 'Australia') {
        return { isValid: false, error: 'Only Australia is currently supported' };
      }
      return { isValid: true };
    }, [])
  };

  /**
   * Schedule Information Validations
   * Groups: Calendly link
   */
  const scheduleValidations = {
    calendlyLink: useCallback((url: string): ValidationResult => {
      if (url.length === 0) {
        return { isValid: false, error: 'Calendly link is required' };
      }

      const calendlyPattern = /^https:\/\/calendly\.com\/[a-zA-Z0-9_-]+/;
      if (!calendlyPattern.test(url)) {
        return { isValid: false, error: 'Invalid Calendly URL format' };
      }

      return { isValid: true };
    }, [])
  };

  /**
   * Fees and payment validations (design-patient-payment-guidance §4.13.4).
   * Money shares its regex with `dollarsToCents`, so the validator and the converter never disagree (D5).
   */
  const feeValidations = {
    money: useCallback((value: string): ValidationResult => {
      const normalised = normaliseMoneyText(value ?? '');
      if (normalised === '') return { isValid: true };
      if (!MONEY_RE.test(normalised)) return { isValid: false, error: 'Enter a dollar amount, e.g. 120' };
      return { isValid: true };
    }, []),

    noticeHours: useCallback((value: string): ValidationResult => {
      const text = (value ?? '').trim();
      if (text === '') return { isValid: true };
      if (!/^\d+$/.test(text) || Number(text) > 720) {
        return { isValid: false, error: 'Enter a whole number of hours, 0 to 720' };
      }
      return { isValid: true };
    }, []),

    paymentNotes: useCallback((value: string): ValidationResult => {
      if ((value ?? '').trim().length > 1000) return { isValid: false, error: 'Keep notes under 1,000 characters' };
      return { isValid: true };
    }, []),
  };

  // ============================================================================
  // STEP VALIDATION LOGIC
  // ============================================================================

  /**
   * Step-specific validation functions
   * Organized by registration workflow steps
   */
  const stepValidations = {
    registration: useCallback((data: Partial<DieticianRegistrationData>): ValidationResult => {
      const errors: string[] = [];

      const emailResult = personalValidations.email(data.email || '');
      if (!emailResult.isValid) {
        errors.push(emailResult.error!);
      }

      const passwordResult = personalValidations.password(data.password || '');
      if (!passwordResult.isValid) {
        errors.push(passwordResult.error!);
      }

      const firstNameResult = personalValidations.name(data.firstName || '', 'First name');
      if (!firstNameResult.isValid) {
        errors.push(firstNameResult.error!);
      }

      const lastNameResult = personalValidations.name(data.lastName || '', 'Last name');
      if (!lastNameResult.isValid) {
        errors.push(lastNameResult.error!);
      }

      if (errors.length > 0) {
        return { isValid: false, error: errors.join(', ') };
      }

      return { isValid: true };
    }, [personalValidations.email, personalValidations.password, personalValidations.name]),

    profile: useCallback((data: Partial<DieticianRegistrationData>): ValidationResult => {
      const errors: string[] = [];

      const areasResult = professionalValidations.areasOfPractice(data.areasOfPractice || []);
      if (!areasResult.isValid) {
        errors.push(areasResult.error!);
      }

      const languagesResult = professionalValidations.languages(data.languagesSpoken || []);
      if (!languagesResult.isValid) {
        errors.push(languagesResult.error!);
      }

      const phoneResult = professionalValidations.phone(data.phone || {} as PhoneData);
      if (!phoneResult.isValid) {
        errors.push(phoneResult.error!);
      }

      const apdResult = professionalValidations.apdNumber(data.apdNumber || '');
      if (!apdResult.isValid) {
        errors.push(apdResult.error!);
      }

      if (errors.length > 0) {
        return { isValid: false, error: errors.join(', ') };
      }

      return { isValid: true };
    }, [professionalValidations.areasOfPractice, professionalValidations.phone, professionalValidations.apdNumber]),

    schedule: useCallback((data: Partial<DieticianRegistrationData>): ValidationResult => {
      // Support both calendlyLink (test format) and calendlySchedulingUri (interface format)
      const dataWithCalendlyLink = data as Partial<DieticianRegistrationData & { calendlyLink?: string }>;
      const calendlyUrl = dataWithCalendlyLink.calendlyLink || data.calendlySchedulingUri || '';
      return scheduleValidations.calendlyLink(calendlyUrl);
    }, [scheduleValidations.calendlyLink])
  };

  // ============================================================================
  // PUBLIC API - BACKWARD COMPATIBLE
  // ============================================================================

  // Main step validation function
  const validateStep = useCallback((step: number, formData: Partial<DieticianRegistrationData>): ValidationResult => {
    switch (step) {
      case 1:
        return stepValidations.registration(formData);
      case 2:
        return stepValidations.profile(formData);
      case 3:
        return stepValidations.schedule(formData);
      case 4:
        return { isValid: true }; // Confirmation step has no validation
      default:
        return { isValid: false, error: 'Invalid step' };
    }
  }, [stepValidations.registration, stepValidations.profile, stepValidations.schedule]);

  // Individual validation functions (for backward compatibility)
  const validateEmail = personalValidations.email;
  const validatePassword = personalValidations.password;
  const validateName = personalValidations.name;
  const validateAPDNumber = professionalValidations.apdNumber;
  const validateMoney = feeValidations.money;
  const validateNoticeHours = feeValidations.noticeHours;
  const validatePaymentNotes = feeValidations.paymentNotes;
  const validateAreasOfPractice = professionalValidations.areasOfPractice;
  const validateLanguages = professionalValidations.languages;
  const validatePhone = professionalValidations.phone;
  const validateState = addressValidations.state;
  const validateCalendlyLink = scheduleValidations.calendlyLink;

  // Address validations (kept for backward compatibility)
  const validateStreetAddress = addressValidations.streetAddress;
  const validateCity = addressValidations.city;
  const validateAddressState = addressValidations.state; // Alias for validateState
  const validatePostalCode = addressValidations.postalCode;
  const validateCountry = addressValidations.country;

  return {
    // Core validation functions (used by tests)
    validateEmail,
    validateAPDNumber,
    validateCalendlyLink,
    validateAreasOfPractice,
    validateState,
    validatePhone,
    validatePassword,
    validateName,
    validateStep,
    validateLanguages,
    validateMoney,
    validateNoticeHours,
    validatePaymentNotes,

    // Address validation functions (for backward compatibility)
    validateStreetAddress,
    validateCity,
    validateAddressState,
    validatePostalCode,
    validateCountry
  };
}